import { createCalendar } from "@internationalized/date";
import {
  RENTAL_AREAS_AVAILABILITIES_STATES_VALUE_BOOKED,
  RENTAL_AREAS_AVAILABILITIES_STATES_VALUE_UNAVAILABLE,
} from "@raiden/library/constants/rentalArea/availability";
import { useApi } from "@raiden/library/hooks/useApi";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import { useDatePicker } from "@react-aria/datepicker";
import { useLocale } from "@react-aria/i18n";
import { useCalendarState } from "@react-stately/calendar";
import { useDatePickerState } from "@react-stately/datepicker";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Calendar } from "./Calendar";

/**
 * Format a date object to a string.
 * @param {object} param0
 * @param {typeof import("@internationalized/date").CalendarDateTime} param0.selectedDate
 * @returns {string | null}
 */
function fromDateObjectToString({ selectedDate }) {
  return selectedDate ? selectedDate.toString() : "";
}

/**
 * @typedef Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 * @property {import("@internationalized/date").CalendarDateTime|import("@internationalized/date").CalendarDate} minValue
 * @property {() => void} [onClick]
 * @property {(event: any) => void} onChange
 */
/**
 * @param {Props} props
 */
export function DatePicker(props) {
  const { rentalArea, onClick, onChange } = props;

  let state = useDatePickerState(props);

  let { locale } = useLocale();

  let inputGroupRef = useRef(null);
  let { calendarProps } = useDatePicker(props, state, inputGroupRef);

  /** @type {import("@raiden/library/types/common/DatePicker").UnavailableRanges[]} */
  const DefaultValueUnavailableRanges = [];

  const [unavailableRanges, setUnavailableRanges] = useState(
    DefaultValueUnavailableRanges,
  );

  let localState = useCalendarState({
    locale,
    createCalendar,
    onChange: handleChange,
  });

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/rentalArea/Availability").RentalAreaAvailability[]>} */
  const { swrResponse: swrRentalAreaAvailabilityResponse } = useApi(
    generateApiUrl({
      id: "@api.rental_areas.availabilities.viewAny",
      query: {
        area_id: [rentalArea.id],
        min_date: localState.visibleRange.start.toString(),
        max_date: localState.visibleRange.end.toString(),
        include_booking_unavailability: true,
        per_page: 250,
      },
    }),
  );

  /**
   * Mise à jour des jours indisponibles en fonction des periodes fermées
   */
  useEffect(() => {
    if (swrRentalAreaAvailabilityResponse.data?.data) {
      const dates = [];
      swrRentalAreaAvailabilityResponse.data?.data?.[0]?.periods?.forEach(
        (period) => {
          if (
            period.state ===
            RENTAL_AREAS_AVAILABILITIES_STATES_VALUE_UNAVAILABLE
          ) {
            dates.push({
              from: dayjs.utc(period.start_at),
              to: dayjs.utc(period.end_at),
              state: RENTAL_AREAS_AVAILABILITIES_STATES_VALUE_UNAVAILABLE,
            });
          }
        },
      );
      let optimizedDates = [];

      // Effectue une mutation
      dates.sort((a, b) => a.from - b.from);
      /**
       * @typedef {Object} OptimizedDates
       * @property {import("dayjs").Dayjs} from
       * @property {import("dayjs").Dayjs} to
       * @property {string} [state]
       */
      dates.forEach(
        /** @param {OptimizedDates} item */
        (item) => {
          if (optimizedDates.length === 0) {
            optimizedDates.push(item);
          } else {
            /**
             * @type {OptimizedDates}
             */
            const lastItem = optimizedDates[optimizedDates.length - 1];
            const currentItemFrom = item.from.clone().subtract(1, "minute");
            if (
              lastItem.state ===
                RENTAL_AREAS_AVAILABILITIES_STATES_VALUE_BOOKED &&
              item.state === RENTAL_AREAS_AVAILABILITIES_STATES_VALUE_BOOKED
            ) {
              if (lastItem.to.isSame(currentItemFrom, "minute")) {
                lastItem.to = item.to;
              } else {
                optimizedDates.push(item);
              }
            } else {
              optimizedDates.push(item);
            }
          }
        },
      );

      setUnavailableRanges(optimizedDates);
    }
  }, [swrRentalAreaAvailabilityResponse.data?.data]);

  function handleChange(selectedDate) {
    onChange({
      target: {
        value: selectedDate ? fromDateObjectToString({ selectedDate }) : "",
      },
    });
  }

  return (
    <Calendar
      {...calendarProps}
      localState={localState}
      rentalArea={rentalArea}
      unavailableRanges={unavailableRanges}
      state={state}
      onChange={handleChange}
      onClick={onClick}
    />
  );
}
